import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { useEffect, useState } from "react";
import FruitifyLogo from "../../../shared-components/FruitifyLogo";
import {
  RegistrationCard,
  FruitifyStack,
  FruitifyContainer,
} from "../../../shared-components/Containers";
import {
  Chip,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApiClient } from "../../../api";
import GeneralLoading from "../../../shared-components/Loading";
import { enqueueSnackbar } from "notistack";
import SchoolSearchAutocomplete from "../../school/components/SchoolSearchAutocomplete";
import { useTrackingAnalytics } from "../../../hooks/useTrackingAnalytics";
import Autocomplete from "@mui/material/Autocomplete";
import { useAuthenticationStore } from "../../../stores/auth";
import {
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import TermsAcceptance from "../components/TermsAcceptance";
import { schoolMeetingUrl } from "../../help/SchoolAccessDialog";

const validateName = (name, fieldName) => {
  if (!name) {
    return `${fieldName} must be provided`;
  } else if (!/^[A-Za-z]+$/.test(name)) {
    return `${fieldName} can only contain letters`;
  } else {
    return "";
  }
};

const FreemiumRequestDialog = ({
  open,
  onClose,
  supportEmail,
  userEmail,
  tempPassword,
}) => {
  const ApiClient = useApiClient();
  const navigate = useNavigate();
  const { setUser, updateUserInfo } = useAuthenticationStore((state) => state);
  const { identifyUser } = useTrackingAnalytics();

  const onSuccessfulLogin = async (data) => {
    const { token, refreshToken, refreshExpiresIn } = data;
    setUser({
      token,
      refreshToken,
      refreshExpiresIn,
    });
    const userData = await ApiClient.users.me();
    identifyUser(userData.email, false);
    updateUserInfo(userData);
    setTimeout(() => {
      navigate(`/`, { replace: true });
    }, 100);
  };

  const handleLogin = async () => {
    try {
      const data = await ApiClient.users.login({
        email: userEmail,
        password: tempPassword,
      });
      onSuccessfulLogin(data);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          Your free account is ready 🎉
          <IconButton onClick={onClose} sx={{ border: 0 }}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <p>
          Thanks — your Fruitify account has been created and you can start
          using it straight away.
        </p>

        <p>
          You’ve been given access to our free tier. If your school would like
          to unlock additional benefits, you can request school approval at any
          time.
        </p>

        <p>We’ve sent you a confirmation email with your account details.</p>

        <Typography component="p" variant="caption" color="text.secondary">
          If you don’t see our email, please check your inbox, “Other” tab, or
          spam folder — sometimes school email systems filter first-time
          messages.
        </Typography>

        <p>
          If you need help, you can contact us directly at{" "}
          <Link
            href={`mailto:${supportEmail}?subject=Fruitify Account Support`}
          >
            {supportEmail}
          </Link>
          .
        </p>
      </DialogContent>

      <DialogActions>
        <Button
          variant="action"
          onClick={() => window.open(`mailto:${supportEmail}`)}
        >
          Email Support
        </Button>
        <Button variant="contained" onClick={handleLogin}>
          Start using Fruitify
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function FreemiumRequest() {
  const [errors, setErrors] = useState({});

  const [termsAndConditions, setTermsAndConditions] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [yearGroups, setYearGroups] = useState([]);
  const [selectedYearGroups, setSelectedYearGroup] = useState([]);
  const [subject, setSubject] = useState("");
  const { recordMarketingEvent } = useTrackingAnalytics();
  const [openRedirectDialog, setOpenRedirectDialog] = useState(false);
  const [isTeacherChecked, setIsTeacherChecked] = useState(false);
  const [supportEmail, setSupportEmail] = useState("");
  const [tempPassword, setTempPassword] = useState("");
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    dataOfficerEmail: "",
    school: null,
    referralCode: searchParams.get("ref") || "",
  });

  const [loading, setLoading] = useState(false);
  const apiClient = useApiClient();

  useEffect(() => {
    recordMarketingEvent("freemium_request", {}, true);
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await apiClient.core.getRegistrationInformation();
        setTermsAndConditions(result?.termsAndConditions || null);
      } catch (error) {
        console.error("Failed to fetch terms and conditions:", error);
        setTermsAndConditions(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await apiClient.core.getAllActiveSubjects();
        setSubjects(result?.allActiveSubjects || []);
        setYearGroups(result?.allYearGroups || []);
      } catch (error) {
        console.error("Failed to fetch subjects and year groups", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const onRegister = () => {
    if (validateData()) {
      handleSubmit();
    }
  };

  const handleChange = (field, value) => {
    if (field === "school" && typeof value === "string") {
      value = value
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateData = () => {
    const newErrors = {};

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Incorrect or missing email";
    }

    const firstNameError = validateName(formData.firstName, "First name");
    if (firstNameError) {
      newErrors.firstName = firstNameError;
    }
    const surnameError = validateName(formData.lastName, "Surname");
    if (surnameError) {
      newErrors.lastName = firstNameError;
    }
    if (!formData.school) {
      newErrors.school = "School is required";
    }

    const isValid = Object.keys(newErrors).length === 0;
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      console.log(selectedYearGroups);
      const data = await apiClient.users.requestFreeAccount({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        subject: subject.id,
        yearGroups: selectedYearGroups.map((yg) => yg),
        school: formData.school.id,
        dataOfficerEmail: formData.dataOfficerEmail,
        referralCode: formData.referralCode,
      });
      if (data?.success && data?.supportEmail && data?.tempPassword) {
        setSupportEmail(data.supportEmail);
        setTempPassword(data.tempPassword);
        setOpenRedirectDialog(true);
      } else {
        enqueueSnackbar(data?.message || "Something went wrong", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Error registering, please try again", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <GeneralLoading />;
  }

  return (
    <FruitifyContainer>
      <FreemiumRequestDialog
        open={openRedirectDialog}
        onClose={() => setOpenRedirectDialog(false)}
        supportEmail={supportEmail}
        userEmail={formData.email.toLowerCase()}
        tempPassword={tempPassword}
      />
      <FruitifyStack
        direction="column"
        justifyContent="space-between"
        sx={{ height: "100%" }}
      >
        <RegistrationCard variant="outlined">
          <Stack direction={"row"} justifyContent={"center"}>
            <FruitifyLogo />
          </Stack>
          <Typography
            component="h1"
            variant="h4"
            sx={{
              width: "100%",
              fontSize: "clamp(2rem, 10vw, 2.15rem)",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            Join for free!
          </Typography>
          <Typography variant={"subtitle1"}>
            Fruitify is free for schools and colleges.
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2">Prefer to talk to us first?</Typography>
            <Button
              component="a"
              href={schoolMeetingUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              color="primary"
            >
              Book a call
            </Button>
          </Stack>
          <Typography variant={"body2"}>
            We just need a few details to get started. Please ensure you use
            your school email address.
          </Typography>
          <Box
            component="form"
            onSubmit={(e) => handleSubmit(e)}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 2,
            }}
          >
            {loading ? (
              <GeneralLoading />
            ) : (
              <Grid container spacing={2}>
                <Grid item size={12}>
                  <SchoolSearchAutocomplete
                    school={formData.school}
                    onChange={(_, value) => handleChange("school", value)}
                  />
                </Grid>{" "}
                <Grid item size={12}>
                  <Autocomplete
                    sx={{ flexGrow: 1 }}
                    options={subjects}
                    getOptionLabel={(option) => option.name}
                    value={subject || null} // single selection
                    onChange={(_, newValue) => setSubject(newValue)}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Subjects"
                        variant="outlined"
                      />
                    )}
                    disableClearable={false} // optional: allow clearing selection
                    freeSolo={false} // prevents entering arbitrary text
                  />
                </Grid>
                <Grid item size={12}>
                  <FormControl fullWidth>
                    <InputLabel id="year-group-label">Year Group</InputLabel>

                    <Select
                      labelId="year-group-label"
                      multiple
                      value={selectedYearGroups}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedYearGroup(
                          typeof value === "string" ? value.split(",") : value,
                        );
                      }}
                      label="Year Group"
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((id) => {
                            const year = yearGroups.find((y) => y.id === id);
                            return (
                              <Chip
                                key={id}
                                label={`Year ${year?.yearGroup}`}
                                size="small"
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {yearGroups.map((year) => (
                        <MenuItem key={year.id} value={year.id}>
                          <Checkbox
                            checked={selectedYearGroups.indexOf(year.id) > -1}
                          />
                          <ListItemText primary={`Year ${year.yearGroup}`} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="First Name"
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    value={formData.firstName}
                    autoComplete="given-name"
                    onChange={(e) => handleChange("firstName", e.target.value)}
                    required
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                  <TextField
                    label="Surname"
                    autoComplete="family-name"
                    value={formData.lastName}
                    onChange={(e) => handleChange("lastName", e.target.value)}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    required
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                <Grid item size={12}>
                  <FormControl fullWidth>
                    <TextField
                      label="Email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      error={!!errors.email}
                      helperText={errors.email}
                      autoComplete="email"
                      autoFocus
                      required
                      fullWidth
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
                <Grid item size={12}>
                  <FormControl fullWidth>
                    <TextField
                      label="Data Officer Email (optional)"
                      value={formData.dataOfficerEmail}
                      onChange={(e) =>
                        handleChange("dataOfficerEmail", e.target.value)
                      }
                      error={!!errors.dataOfficerEmail}
                      helperText={errors.dataOfficerEmail}
                      autoComplete="email"
                      autoFocus
                      required
                      fullWidth
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
                <Grid item size={12}>
                  <FormControl fullWidth>
                    <TextField
                      label="Referral code (optional)"
                      value={formData.referralCode}
                      onChange={(e) =>
                        handleChange("referralCode", e.target.value)
                      }
                      fullWidth
                    />
                  </FormControl>
                </Grid>
                <Grid item size={12}>
                  <FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isTeacherChecked}
                          onChange={(e) =>
                            setIsTeacherChecked(e.target.checked)
                          }
                          required
                        />
                      }
                      label="I am a teacher or educator"
                    />
                  </FormControl>
                  <Typography variant={"body2"}>
                    <strong>
                      *Fruitify does not currently offer a direct-to-student
                      product
                    </strong>
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>
          <TermsAcceptance
            termsUrl={termsAndConditions?.preSignedUrl}
            checked={acceptedTerms}
            onChange={setAcceptedTerms}
          />
          <Button
            onClick={onRegister}
            color={"secondary"}
            variant="contained"
            disabled={
              !subject ||
              !selectedYearGroups ||
              !selectedYearGroups.length > 0 ||
              !formData.school ||
              !isTeacherChecked ||
              !acceptedTerms
            }
          >
            Start
          </Button>
        </RegistrationCard>
      </FruitifyStack>
    </FruitifyContainer>
  );
}
